import { useCallback, useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import { useSuccessToast } from '~/hooks/useSuccessToast'

/**
 * 保存 toast の debounce (ms)。
 *
 * design (`design/settings.html` line 137-161) の `eclNotifySaved` と同値。
 * design のコメント曰く「color picker の input 連続発火 / 「全エリア」 master の
 * cascade で多重発火するため、 debounce で 1 件に集約する」。
 */
export const SAVE_TOAST_DEBOUNCE_MS = 450

/** 保存 toast の表示時間 (ms)。 design の `duration: 2500` と同値。 */
export const SAVE_TOAST_DURATION_MS = 2500

/**
 * 個人設定の「保存しました」 toast を出す hook。
 *
 * 個人設定はカード単位の即時保存 (保存ボタンなし) のため、「いつ保存されたか
 * 分からない」 を防ぐ feedback として保存成功時に toast を出す
 * (design/settings.html line 137-161、 2026-06-25 のデザイン判断)。
 *
 * 返り値の `notifySaved` は {@link SAVE_TOAST_DEBOUNCE_MS} で debounce する。
 * 連続保存 (color picker のドラッグ / 「全エリア」 の cascade / 設定を続けて
 * 変更) でも toast は 1 件に集約される。
 *
 * unmount 後は toast を出さない (= 画面を離れた後に別ページで「保存しました」
 * が出るのを防ぐ)。 pending timer の clear だけでは、 unmount cleanup で flush
 * された mutation の成功が後から `notifySaved` を呼ぶ経路 (usePersonalSettings
 * の debounce flush) を塞げないため、 mount 状態も見る。
 */
export function useSaveSuccessToast(): () => void {
  const { t } = useTranslation()
  const successToast = useSuccessToast()

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])

  return useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      if (!isMountedRef.current) return
      successToast({
        title: t('page:settings.personal.saveSuccess'),
        duration: SAVE_TOAST_DURATION_MS,
        closable: true,
      })
    }, SAVE_TOAST_DEBOUNCE_MS)
  }, [successToast, t])
}
